import React, { useState, useEffect } from "react";

function VisitsReports({ schedules, qaUsers, centers, onDownloadReport }) {
  const [filters, setFilters] = useState({
    user: "ALL",
    status: "ALL",
    fromDate: "",
    toDate: "",
  });

  const [filteredSchedules, setFilteredSchedules] = useState([]);

  // Apply filters
  useEffect(() => {
    let filtered = [...schedules];

    // Filter by QA User
    if (filters.user !== "ALL") {
      filtered = filtered.filter(s => s.qaUserId === filters.user);
    }

    // Filter by Status
    if (filters.status !== "ALL") {
      filtered = filtered.filter(s => s.status === filters.status);
    }

    // Filter by Date Range
    if (filters.fromDate) {
      filtered = filtered.filter(s => {
        const visitDate = new Date(s.visitDate);
        const fromDate = new Date(filters.fromDate);
        return visitDate >= fromDate;
      });
    }

    if (filters.toDate) {
      filtered = filtered.filter(s => {
        const visitDate = new Date(s.visitDate);
        const toDate = new Date(filters.toDate);
        return visitDate <= toDate;
      });
    }

    setFilteredSchedules(filtered);
  }, [schedules, filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({
      user: "ALL",
      status: "ALL",
      fromDate: "",
      toDate: "",
    });
  };

  const getCenterName = (centerId) => {
    const center = centers.find(c => c.id === centerId);
    return center ? center.name : "Unknown Center";
  };

  const getCityName = (centerId) => {
    const center = centers.find(c => c.id === centerId);
    return center ? center.city : "Unknown City";
  };

  const getUserName = (userId) => {
    const user = qaUsers.find(u => String(u.id) === String(userId));
    return user ? `${user.firstName} ${user.lastName}` : "Unknown User";
  };

  const getStatusBadge = (status) => {
    const baseStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.3px',
      textTransform: 'uppercase'
    };

    const styles = {
      Pending: {
        background: 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)',
        color: '#856404',
        border: '1px solid #ffd166'
      },
      "In Progress": {
        background: 'linear-gradient(135deg, #cfe2ff 0%, #a5c8ff 100%)',
        color: '#084298',
        border: '1px solid #74c0fc'
      },
      Completed: {
        background: 'linear-gradient(135deg, #d4edda 0%, #a8e6a3 100%)',
        color: '#155724',
        border: '1px solid #8fd19e'
      },
      Missed: {
        background: 'linear-gradient(135deg, #f8d7da 0%, #ffb8b8 100%)',
        color: '#842029',
        border: '1px solid #ff9b9b'
      }
    };

    const style = styles[status] || {
      background: '#f3f5f7',
      color: '#6c757d',
      border: '1px solid #e2e8f0'
    };

    return (
      <span style={{ ...baseStyle, ...style }}>
        {status === "Pending" && <i className="fas fa-clock" style={{ fontSize: '0.65rem' }}></i>}
        {status === "In Progress" && <i className="fas fa-play-circle" style={{ fontSize: '0.65rem' }}></i>}
        {status === "Completed" && <i className="fas fa-check-circle" style={{ fontSize: '0.65rem' }}></i>}
        {status === "Missed" && <i className="fas fa-times-circle" style={{ fontSize: '0.65rem' }}></i>}
        {status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  // Calculate stats
  const totalVisits = filteredSchedules.length;
  const completedVisits = filteredSchedules.filter(s => s.status === "Completed").length;
  const pendingVisits = filteredSchedules.filter(s => s.status === "Pending").length;
  const inProgressVisits = filteredSchedules.filter(s => s.status === "In Progress").length;
  const missedVisits = filteredSchedules.filter(s => s.status === "Missed").length;

  return (
    <div className="row g-3">
      {/* Left Column - Filters & Legend */}
      <div className="col-lg-3">
        {/* Filters Card */}
        <div className="form-section" style={{ 
          border: '1px solid #e0e6ed',
          borderRadius: '12px',
          padding: '1.25rem 1.5rem',
          background: 'white',
          marginBottom: '1.5rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}>
          <div className="form-section-title" style={{ 
            fontWeight: 700,
            fontSize: '1rem',
            marginBottom: '1rem',
            color: '#2c3e50',
            borderBottom: '2px solid #f1f5f9',
            paddingBottom: '0.75rem'
          }}>
            <i className="fas fa-filter mr-2" style={{ color: '#4f46e5' }}></i>
            Filters
          </div>
          
          <div className="mb-3">
            <label className="form-label" style={{ 
              marginBottom: '0.5rem',
              fontWeight: 500,
              lineHeight: 1.2,
              fontSize: '0.85rem',
              color: '#4a5568'
            }}>
              QA User
            </label>
            <select
              value={filters.user}
              onChange={(e) => handleFilterChange("user", e.target.value)}
              className="form-select form-select-sm"
              style={{
                display: 'block',
                width: '100%',
                padding: '0.5rem 0.75rem',
                fontSize: '0.875rem',
                lineHeight: 1.5,
                color: '#2d3748',
                backgroundColor: '#fff',
                backgroundClip: 'padding-box',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                transition: 'all 0.2s ease'
              }}
            >
              <option value="ALL">All QA Users</option>
              {qaUsers.map(user => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-3">
            <label className="form-label" style={{ 
              marginBottom: '0.5rem',
              fontWeight: 500,
              lineHeight: 1.2,
              fontSize: '0.85rem',
              color: '#4a5568'
            }}>
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              className="form-select form-select-sm"
              style={{
                display: 'block',
                width: '100%',
                padding: '0.5rem 0.75rem',
                fontSize: '0.875rem',
                lineHeight: 1.5,
                color: '#2d3748',
                backgroundColor: '#fff',
                backgroundClip: 'padding-box',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                transition: 'all 0.2s ease'
              }}
            >
              <option value="ALL">All Status</option>
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Missed">Missed</option>
            </select>
          </div>

          <div className="mb-3">
            <label className="form-label" style={{ 
              marginBottom: '0.5rem',
              fontWeight: 500,
              lineHeight: 1.2,
              fontSize: '0.85rem',
              color: '#4a5568'
            }}>
              From Date
            </label>
            <input
              type="date"
              value={filters.fromDate}
              onChange={(e) => handleFilterChange("fromDate", e.target.value)}
              className="form-control form-control-sm"
              style={{
                display: 'block',
                width: '100%',
                padding: '0.5rem 0.75rem',
                fontSize: '0.875rem',
                lineHeight: 1.5,
                color: '#2d3748',
                backgroundColor: '#fff',
                backgroundClip: 'padding-box',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                transition: 'all 0.2s ease'
              }}
            />
          </div>

          <div className="mb-3">
            <label className="form-label" style={{ 
              marginBottom: '0.5rem',
              fontWeight: 500,
              lineHeight: 1.2,
              fontSize: '0.85rem',
              color: '#4a5568'
            }}>
              To Date
            </label>
            <input
              type="date"
              value={filters.toDate}
              onChange={(e) => handleFilterChange("toDate", e.target.value)}
              className="form-control form-control-sm"
              style={{
                display: 'block',
                width: '100%',
                padding: '0.5rem 0.75rem',
                fontSize: '0.875rem',
                lineHeight: 1.5,
                color: '#2d3748',
                backgroundColor: '#fff',
                backgroundClip: 'padding-box',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                transition: 'all 0.2s ease'
              }}
            />
          </div>

          <button
            onClick={resetFilters}
            className="btn btn-outline-gray btn-sm w-100"
            style={{
              border: '1px solid #e2e8f0',
              color: '#64748b',
              fontWeight: 500,
              padding: '0.5rem',
              borderRadius: '6px',
              fontSize: '0.8125rem',
              marginTop: '0.5rem',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f1f5f9';
              e.currentTarget.style.borderColor = '#cbd5e0';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = '#e2e8f0';
            }}
          >
            <i className="fas fa-redo-alt mr-2" style={{ fontSize: '0.75rem' }}></i>
            Reset Filters
          </button>
        </div>

        {/* Legend Card */}
        <div className="form-section" style={{ 
          border: '1px solid #e0e6ed',
          borderRadius: '12px',
          padding: '1.25rem 1.5rem',
          background: 'white',
          marginBottom: '1rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}>
          <div className="form-section-title" style={{ 
            fontWeight: 700,
            fontSize: '1rem',
            marginBottom: '1rem',
            color: '#2c3e50',
            borderBottom: '2px solid #f1f5f9',
            paddingBottom: '0.75rem'
          }}>
            <i className="fas fa-info-circle mr-2" style={{ color: '#4f46e5' }}></i>
            Status Legend
          </div>
          
          <div className="d-flex flex-column gap-2" style={{ fontSize: '0.875rem' }}>
            <div className="d-flex align-items-center gap-2">
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)',
                border: '1px solid #ffd166',
                flexShrink: 0
              }}></div>
              <div>
                <div style={{ fontWeight: 600, color: '#2d3748', fontSize: '0.85rem' }}>Pending</div>
                <div style={{ fontSize: '0.75rem', color: '#718096' }}>Not started</div>
              </div>
            </div>
            
            <div className="d-flex align-items-center gap-2">
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #cfe2ff 0%, #a5c8ff 100%)',
                border: '1px solid #74c0fc',
                flexShrink: 0
              }}></div>
              <div>
                <div style={{ fontWeight: 600, color: '#2d3748', fontSize: '0.85rem' }}>In Progress</div>
                <div style={{ fontSize: '0.75rem', color: '#718096' }}>Time-in captured</div>
              </div>
            </div>
            
            <div className="d-flex align-items-center gap-2">
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #d4edda 0%, #a8e6a3 100%)',
                border: '1px solid #8fd19e',
                flexShrink: 0
              }}></div>
              <div>
                <div style={{ fontWeight: 600, color: '#2d3748', fontSize: '0.85rem' }}>Completed</div>
                <div style={{ fontSize: '0.75rem', color: '#718096' }}>PDF available</div>
              </div>
            </div>
            
            <div className="d-flex align-items-center gap-2">
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #f8d7da 0%, #ffb8b8 100%)',
                border: '1px solid #ff9b9b',
                flexShrink: 0
              }}></div>
              <div>
                <div style={{ fontWeight: 600, color: '#2d3748', fontSize: '0.85rem' }}>Missed</div>
                <div style={{ fontSize: '0.75rem', color: '#718096' }}>Visit date passed</div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Summary Card */}
        <div className="form-section" style={{ 
          border: '1px solid #e0e6ed',
          borderRadius: '12px',
          padding: '1.25rem 1.5rem',
          background: 'white',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}>
          {/* <div className="form-section-title" style={{ 
            fontWeight: 700,
            fontSize: '1rem',
            marginBottom: '1rem',
            color: '#2c3e50',
            borderBottom: '2px solid #f1f5f9',
            paddingBottom: '0.75rem'
          }}>
            <i className="fas fa-chart-pie mr-2" style={{ color: '#4f46e5' }}></i>
            Filtered Stats
          </div> */}
          
          {/* <div className="d-flex flex-column gap-2">
            <div className="d-flex justify-content-between align-items-center">
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Total Visits</span>
              <span style={{ fontWeight: 700, fontSize: '1rem', color: '#2d3748' }}>{totalVisits}</span>
            </div>
            
            <div className="d-flex justify-content-between align-items-center">
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Completed</span>
              <span style={{ fontWeight: 700, fontSize: '1rem', color: '#16a34a' }}>{completedVisits}</span>
            </div>
            
            <div className="d-flex justify-content-between align-items-center">
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Pending</span>
              <span style={{ fontWeight: 700, fontSize: '1rem', color: '#d97706' }}>{pendingVisits}</span>
            </div>
            
            <div className="d-flex justify-content-between align-items-center">
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>In Progress</span>
              <span style={{ fontWeight: 700, fontSize: '1rem', color: '#2563eb' }}>{inProgressVisits}</span>
            </div>
            
            <div className="d-flex justify-content-between align-items-center">
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Missed</span>
              <span style={{ fontWeight: 700, fontSize: '1rem', color: '#dc2626' }}>{missedVisits}</span>
            </div>
          </div> */}
        </div>
      </div>

      {/* Right Column - Enhanced Visits Table */}
      <div className="col-lg-9">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h6 className="mb-0" style={{ fontSize: '1rem', fontWeight: 700, color: '#2c3e50' }}>
              <i className="fas fa-clipboard-list mr-2" style={{ color: '#4f46e5' }}></i>
                
                All QA Visits ({filteredSchedules.length})
            </h6>
            <div className="small-helper mt-1" style={{ fontSize: '0.75rem', color: '#718096' }}>
              Showing {filteredSchedules.length} of {schedules.length} visits
            </div>
          </div>
          
          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
            <i className="fas fa-info-circle mr-1"></i>
            PDF download generates a sample file
          </div>
        </div>

        {/* Enhanced Stats Cards */}
        {/* <div className="row g-2 mb-3">
          <div className="col-3">
            <div className="kpi" style={{
              border: 'none',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              padding: '1rem',
              color: 'white',
              boxShadow: '0 4px 6px rgba(79, 70, 229, 0.2)'
            }}>
              <div className="value d-flex align-items-center justify-content-between" style={{ 
                fontWeight: 800, 
                fontSize: '1.25rem',
                marginBottom: '0.25rem'
              }}>
                {totalVisits}
                <i className="fas fa-chart-bar" style={{ fontSize: '1rem', opacity: 0.8 }}></i>
              </div>
              <div className="label" style={{ 
                fontSize: '0.75rem',
                opacity: 0.9
              }}>
                Total Visits
              </div>
            </div>
          </div>
          
          <div className="col-3">
            <div className="kpi" style={{
              border: 'none',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
              padding: '1rem',
              color: 'white',
              boxShadow: '0 4px 6px rgba(16, 185, 129, 0.2)'
            }}>
              <div className="value d-flex align-items-center justify-content-between" style={{ 
                fontWeight: 800, 
                fontSize: '1.25rem',
                marginBottom: '0.25rem'
              }}>
                {completedVisits}
                <i className="fas fa-check-circle" style={{ fontSize: '1rem', opacity: 0.8 }}></i>
              </div>
              <div className="label" style={{ 
                fontSize: '0.75rem',
                opacity: 0.9
              }}>
                Completed
              </div>
            </div>
          </div>
          
          <div className="col-3">
            <div className="kpi" style={{
              border: 'none',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
              padding: '1rem',
              color: 'white',
              boxShadow: '0 4px 6px rgba(245, 158, 11, 0.2)'
            }}>
              <div className="value d-flex align-items-center justify-content-between" style={{ 
                fontWeight: 800, 
                fontSize: '1.25rem',
                marginBottom: '0.25rem'
              }}>
                {pendingVisits + inProgressVisits}
                <i className="fas fa-clock" style={{ fontSize: '1rem', opacity: 0.8 }}></i>
              </div>
              <div className="label" style={{ 
                fontSize: '0.75rem',
                opacity: 0.9
              }}>
                Active
              </div>
            </div>
          </div>
          
          <div className="col-3">
            <div className="kpi" style={{
              border: 'none',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)',
              padding: '1rem',
              color: 'white',
              boxShadow: '0 4px 6px rgba(239, 68, 68, 0.2)'
            }}>
              <div className="value d-flex align-items-center justify-content-between" style={{ 
                fontWeight: 800, 
                fontSize: '1.25rem',
                marginBottom: '0.25rem'
              }}>
                {missedVisits}
                <i className="fas fa-exclamation-circle" style={{ fontSize: '1rem', opacity: 0.8 }}></i>
              </div>
              <div className="label" style={{ 
                fontSize: '0.75rem',
                opacity: 0.9
              }}>
                Missed
              </div>
            </div>
          </div>
        </div> */}

        <div className="table-responsive" style={{
          border: '1px solid #e0e6ed',
          borderRadius: '10px',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}>
          <table className="table table-sm align-middle mb-0">
            <thead style={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              borderBottom: '2px solid #e2e8f0'
            }}>
              <tr>
                <th style={{ 
                  width: '42px', 
                  fontSize: '0.7rem', 
                  textTransform: 'uppercase', 
                  color: '#64748b',
                  fontWeight: 600,
                  padding: '0.875rem 1rem',
                  letterSpacing: '0.5px'
                }}>#</th>
                <th style={{ 
                  width: '120px', 
                  fontSize: '0.7rem', 
                  textTransform: 'uppercase', 
                  color: '#64748b',
                  fontWeight: 600,
                  padding: '0.875rem 1rem',
                  letterSpacing: '0.5px'
                }}>
                  {/* <i className="fas fa-id-badge mr-1"></i> */}
                  Schedule ID
                </th>
                <th style={{ 
                  width: '160px', 
                  fontSize: '0.7rem', 
                  textTransform: 'uppercase', 
                  color: '#64748b',
                  fontWeight: 600,
                  padding: '0.875rem 1rem',
                  letterSpacing: '0.5px'
                }}>
                  {/* <i className="fas fa-user mr-1"></i> */}
                  QA User
                </th>
                <th style={{ 
                  fontSize: '0.7rem', 
                  textTransform: 'uppercase', 
                  color: '#64748b',
                  fontWeight: 600,
                  padding: '0.875rem 1rem',
                  letterSpacing: '0.5px'
                }}>
                  {/* <i className="fas fa-school mr-1"></i> */}
                  Center
                </th>
                {/* <th style={{ 
                  width: '140px', 
                  fontSize: '0.7rem', 
                  textTransform: 'uppercase', 
                  color: '#64748b',
                  fontWeight: 600,
                  padding: '0.875rem 1rem',
                  letterSpacing: '0.5px'
                }}>
                  <i className="fas fa-city mr-1"></i>
                  City
                </th> */}
                <th style={{ 
                  width: '130px', 
                  fontSize: '0.7rem', 
                  textTransform: 'uppercase', 
                  color: '#64748b',
                  fontWeight: 600,
                  padding: '0.875rem 1rem',
                  letterSpacing: '0.5px'
                }}>
                  {/* <i className="fas fa-calendar-day mr-1"></i> */}
                  Scheduled Date
                </th>
                <th style={{ 
                  width: '145px', 
                  fontSize: '0.7rem', 
                  textTransform: 'uppercase', 
                  color: '#64748b',
                  fontWeight: 600,
                  padding: '0.875rem 1rem',
                  letterSpacing: '0.5px'
                }}>
                  {/* <i className="fas fa-calendar-check mr-1"></i> */}
                  Actual Date
                </th>
                <th style={{ 
                  width: '120px', 
                  fontSize: '0.7rem', 
                  textTransform: 'uppercase', 
                  color: '#64748b',
                  fontWeight: 600,
                  padding: '0.875rem 1rem',
                  letterSpacing: '0.5px'
                }}>
                  {/* <i className="fas fa-tag mr-1"></i> */}
                  Status
                </th>
                <th style={{ 
                  width: '140px', 
                  fontSize: '0.7rem', 
                  textTransform: 'uppercase', 
                  color: '#64748b',
                  fontWeight: 600,
                  padding: '0.875rem 1rem',
                  letterSpacing: '0.5px'
                }}>
                  {/* <i className="fas fa-file-pdf mr-1"></i> */}
                  Download PDF
                </th>
              </tr>
            </thead>
            
            <tbody>
              {filteredSchedules.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center text-muted py-5" style={{ 
                    background: '#f8fafc',
                    fontSize: '0.875rem'
                  }}>
                    <div className="py-3">
                      <i className="fas fa-search fa-2x mb-3" style={{ color: '#cbd5e0' }}></i>
                      <div>No visits found</div>
                      <div className="small mt-2" style={{ fontSize: '0.75rem' }}>
                        Try adjusting your filters or create new visits
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredSchedules.map((schedule, index) => (
                  <tr key={schedule.id} style={{
                    borderBottom: '1px solid #f1f5f9',
                    background: index % 2 === 0 ? '#fff' : '#fafcff',
                    transition: 'background 0.2s ease'
                  }}
                  className="hover-row"
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f7fafd'}
                  onMouseLeave={(e) => e.currentTarget.style.background = index % 2 === 0 ? '#fff' : '#fafcff'}
                  >
                    <td style={{ 
                      fontSize: '0.8125rem', 
                      color: '#64748b',
                      fontWeight: 500,
                      padding: '0.875rem 1rem'
                    }}>
                      <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '6px',
                        background: '#f1f5f9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        color: '#4f46e5'
                      }}>
                        {index + 1}
                      </div>
                    </td>
                    <td style={{ 
                      fontSize: '0.8125rem', 
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                      padding: '0.875rem 1rem'
                    }}>
                      <div style={{
                        background: '#f8fafc',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid #e2e8f0',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: '#4f46e5'
                      }}>
                        {schedule.id}
                      </div>
                    </td>
                    <td style={{ 
                      fontSize: '0.8125rem', 
                      fontWeight: 500,
                      padding: '0.875rem 1rem'
                    }}>
                      <div className="d-flex align-items-center">
                        <div style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          marginRight: '0.5rem'
                        }}>
                          {getUserName(schedule.qaUserId).charAt(0)}
                        </div>
                        <span style={{ color: '#2d3748' }}>
                          {getUserName(schedule.qaUserId)}
                        </span>
                      </div>
                    </td>
                    <td style={{ 
                      fontSize: '0.8125rem',
                      padding: '0.875rem 1rem'
                    }}>
                      <div>
                        <div style={{ fontWeight: 500, color: '#2d3748' }}>
                          {getCenterName(schedule.centerId)}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#718096' }}>
                          <i className="fas fa-code mr-1" style={{ fontSize: '0.6rem' }}></i>
                          ID: {schedule.centerId}
                        </div>
                      </div>
                    </td>
                    <td style={{ 
                      fontSize: '0.8125rem', 
                      color: '#64748b',
                      padding: '0.875rem 1rem'
                    }}>
                      <div style={{
                        background: '#f8fafc',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid #e2e8f0',
                        fontSize: '0.75rem'
                      }}>
                        {getCityName(schedule.centerId)}
                      </div>
                    </td>
                    <td style={{ 
                      fontSize: '0.8125rem', 
                      color: '#64748b',
                      padding: '0.875rem 1rem',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                    }}>
                      <div style={{
                        background: '#f8fafc',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid #e2e8f0',
                        fontSize: '0.75rem'
                      }}>
                        {schedule.visitDate}
                      </div>
                    </td>
                    <td style={{ 
                      fontSize: '0.8125rem', 
                      color: '#64748b',
                      padding: '0.875rem 1rem',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                    }}>
                      {schedule.actualVisitDate ? (
                        <div style={{
                          background: '#f8fafc',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          border: '1px solid #e2e8f0',
                          fontSize: '0.75rem',
                          color: '#16a34a',
                          fontWeight: 500
                        }}>
                          {schedule.actualVisitDate}
                        </div>
                      ) : (
                        <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                          <i className="fas fa-minus mr-1"></i>
                          Not visited
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '0.875rem 1rem' }}>
                      {getStatusBadge(schedule.status)}
                    </td>
                    <td style={{ padding: '0.875rem 1rem' }}>
                      {schedule.status === "Completed" ? (
                        <button
                          onClick={() => onDownloadReport(schedule)}
                          className="btn btn-outline-danger btn-sm"
                          style={{
                            border: '1px solid #fecaca',
                            color: '#dc2626',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#fee2e2';
                            e.currentTarget.style.borderColor = '#f87171';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.borderColor = '#fecaca';
                          }}
                        >
                          <i className="fas fa-file-pdf"></i>
                          Download
                        </button>
                      ) : (
                        <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                          <i className="fas fa-minus mr-1"></i>
                          Not available
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="small-helper mt-3 p-3" style={{ 
          fontSize: '0.75rem', 
          color: '#718096',
          background: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
          marginTop: '1.5rem'
        }}>
          <div className="d-flex align-items-start">
            <i className="fas fa-lightbulb mr-2 mt-1" style={{ color: '#f59e0b' }}></i>
            <div>
              <div style={{ fontWeight: 500, marginBottom: '2px' }}>Tip:</div>
              <div>Completed visits include PDF reports. In production, reports are generated automatically by SMS Portal.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VisitsReports;